// ─── apiClient.js ───────────────────────────────────────────────────────────
// ONE helper for every request the app sends to our server.
//
// Before this file, every page built its own fetch() call with the server
// address and the sign-in token. That meant every page also had to decide
// what to do when something went wrong, and most didn't. apiFetch() does
// those jobs in one place:
//
//   1. Adds the server address in front of the path you give it.
//   2. Adds the sign-in token (customer or QC) to the request.
//   3. Turns your `body` into JSON for you.
//   4. If the server says "you're not signed in" (401), clears the old token
//      and sends the person to the right sign-in page, so they can come back.
//   5. Reads the reply safely: JSON when the server sends JSON, plain text
//      when it sends text, and null when the reply is empty.
//   6. Turns any failure into an ApiError with a friendly `userMessage`
//      that is safe to show on screen (no technical codes).
//
// Basic use:
//   const rooms = await apiFetch('/api/rooms/my');
//   await apiFetch('/api/rooms', { method: 'POST', body: { room_name: 'Study' } });
//
// Options:
//   method        'GET' (default), 'POST', 'PUT', 'PATCH', 'DELETE'
//   body          any JS object; sent as JSON
//   area          'customer' (default) or 'qc' — which sign-in token to use
//   auth          true (default). false = send no token (e.g. the sign-in call)
//   redirectOn401 true (default). false = let the page handle 401 itself
//   keepalive     true = let the request finish even if the page closes
//   signal        an AbortController signal, to cancel a request

import { getToken, clearToken } from '../auth/token';

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const SIGNIN_PATHS = {
  customer: '/signin',
  qc:       '/qc/login',
};

// ─── ApiError ───────────────────────────────────────────────────────────────
// status      HTTP status code (0 = the server couldn't be reached at all)
// userMessage a plain-English sentence that is safe to show on screen
// serverText  whatever the server sent back (for the console, not the screen)
export class ApiError extends Error {
  constructor(status, userMessage, serverText = '') {
    super(userMessage);
    this.name = 'ApiError';
    this.status = status;
    this.userMessage = userMessage;
    this.serverText = serverText;
  }
}

function messageForStatus(status) {
  if (status === 0)   return "We couldn't reach our servers. Please check your internet connection and try again.";
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return "You don't have access to this.";
  if (status === 404) return "We couldn't find what you were looking for. It may have been removed.";
  if (status === 409) return 'This conflicts with something that already exists.';
  if (status === 413) return 'That file is too large.';
  if (status >= 500)  return 'Something went wrong on our side. Please try again in a moment.';
  return 'Something went wrong. Please try again.';
}

// Use this in a catch block to get a sentence you can show on screen.
//   catch (err) { setError(friendlyMessage(err, "Couldn't load your rooms.")); }
// The fallback is used when the error didn't come from apiFetch.
export function friendlyMessage(err, fallback = 'Something went wrong. Please try again.') {
  if (err instanceof ApiError) return err.userMessage;
  return fallback;
}

// If several requests fail with 401 at the same moment, only redirect once.
let redirecting = false;

function sendToSignIn(area) {
  if (redirecting) return;
  redirecting = true;
  clearToken(area);
  const here = window.location.pathname + window.location.search;
  const next = encodeURIComponent(here);
  window.location.assign(`${SIGNIN_PATHS[area]}?reason=expired&next=${next}`);
}

async function readBody(res) {
  const text = await res.text();
  if (!text) return null;
  const type = res.headers.get('content-type') || '';
  if (type.includes('application/json')) {
    try { return JSON.parse(text); } catch { return text; }
  }
  return text;
}

export async function apiFetch(path, {
  method = 'GET',
  body,
  area = 'customer',
  auth = true,
  redirectOn401 = true,
  keepalive = false,
  signal,
  headers = {},
} = {}) {
  const token = auth ? getToken(area) : null;

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      keepalive,
      signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err; // cancelled on purpose: not a failure
    console.error(`[apiFetch] ${method} ${path} could not reach the server`, err);
    throw new ApiError(0, messageForStatus(0));
  }

  if (res.status === 401 && auth && redirectOn401) {
    sendToSignIn(area);
    throw new ApiError(401, messageForStatus(401));
  }

  if (!res.ok) {
    const serverText = await res.text().catch(() => '');
    console.error(`[apiFetch] ${method} ${path} failed with ${res.status}`, serverText);
    throw new ApiError(res.status, messageForStatus(res.status), serverText);
  }

  return readBody(res);
}
